import { useCallback, useMemo, useState } from 'react';

import type { ConfirmationModalProps } from '@/components/molecules/ConfirmationModal';

/**
 * @returns callback to trigger displaying confirmation modal and the props to be applied to ConfirmationModal component
 * @example
 * const confirmation = useConfirmation()
 * ...
 * const confirmed = await confirmation.requestConfirmation('Title','Are you sure?')
 * ...
 * return <ConfirmationModal {...confirmation.modalProps} />
 */
export const useConfirmation = () => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [resolvePromise, setResolvePromise] = useState<(value: boolean) => void>(() => {});

  /**
   * Displays confirmation modal and returns promise with user response.
   * @returns Promise with boolean saying whether it was confirmed.
   */
  const requestConfirmation = (
    confirmationTitle: string,
    confirmationMessage: string,
  ): Promise<boolean> => {
    setTitle(confirmationTitle);
    setMessage(confirmationMessage);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      setResolvePromise(() => resolve);
    });
  };

  const onConfirm = useCallback(() => {
    setOpen(false);
    resolvePromise(true);
  }, [resolvePromise]);

  const onReject = useCallback(() => {
    setOpen(false);
    resolvePromise(false);
  }, [resolvePromise]);

  const modalProps = useMemo<ConfirmationModalProps>(
    () => ({
      onConfirm,
      onClose: onReject,
      open,
      title,
      warningMessage: message,
    }),
    [title, message, open, onConfirm, onReject],
  );

  return { requestConfirmation, modalProps };
};
